import { ArrowLeftIcon, CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { ResultNavigation } from '@entities/search-run';

import styles from './VehicleNavigation.module.css';

interface VehicleNavigationProps {
  /** Адрес списка, из которого открыта карточка, — с прогоном, порядком, «Meine Favoriten» и страницей. */
  backTo: string;
  navigation: ResultNavigation;
  /** Адрес соседнего автомобиля — с теми же параметрами списка. */
  vehicleHref: (vehicleId: string, position: number) => string;
}

/**
 * «Zurück zur Liste» и «Auto 3 von 100» ‹ › (FE-587, макет 04).
 *
 * Номер и соседи считает backend **той же функцией порядка, что и список**
 * (BE-580): иначе «Auto 3 von 100» и стрелки разошлись бы с тем, что человек
 * видел. `position = null` — автомобиля нет в списке при этих параметрах
 * (например, «Meine Favoriten» включено, а отметку сняли): тогда ни номера, ни
 * стрелок — показать «Auto ? von 3» значило бы выдумать место.
 *
 * Номер — не ранг: при случайном порядке витрины это место в перестановке, а
 * не в рейтинге, и рядом с ним нет ни Match Score, ни «лучший».
 */
export function VehicleNavigation({ backTo, navigation, vehicleHref }: VehicleNavigationProps) {
  const { t } = useTranslation();
  const { position, total, previous_vehicle_id: previous, next_vehicle_id: next } = navigation;

  return (
    <nav className={styles.nav} aria-label={t('results.detail.nav.label')}>
      <Link to={backTo} className={styles.back}>
        <ArrowLeftIcon size={16} weight="bold" aria-hidden="true" />
        {t('results.detail.nav.back')}
      </Link>

      {position === null ? null : (
        <div className={styles.pager}>
          <p className={`${styles.position} tabular`}>
            {t('results.detail.nav.position', { position, total })}
          </p>
          {previous === null ? (
            <span className={`${styles.arrow} ${styles.arrowDisabled}`} aria-hidden="true">
              <CaretLeftIcon size={18} weight="bold" />
            </span>
          ) : (
            <Link
              to={vehicleHref(previous, position - 1)}
              className={styles.arrow}
              aria-label={t('results.detail.nav.previous')}
              title={t('results.detail.nav.previous')}
            >
              <CaretLeftIcon size={18} weight="bold" aria-hidden="true" />
            </Link>
          )}
          {next === null ? (
            <span className={`${styles.arrow} ${styles.arrowDisabled}`} aria-hidden="true">
              <CaretRightIcon size={18} weight="bold" />
            </span>
          ) : (
            <Link
              to={vehicleHref(next, position + 1)}
              className={styles.arrow}
              aria-label={t('results.detail.nav.next')}
              title={t('results.detail.nav.next')}
            >
              <CaretRightIcon size={18} weight="bold" aria-hidden="true" />
            </Link>
          )}
        </div>
      )}
    </nav>
  );
}
